import { Getter } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { useCallback, useMemo } from 'react';
import { IFundDataModel } from '../../../data-models/fund.data-model';
import { ViewComponentUuid, ViewSectionUuid } from '../../../data-models/view-config.data-model';
import { ViewModel } from '../../../models/view.model';
import { selectedViewPFLoadableJ } from '../state/ViewStateJ';
import { useUpdateSelectedView } from './useViewActions';

function readSelectedViewSync(get: Getter): ViewModel | null {
  const loadable = get(selectedViewPFLoadableJ);
  return loadable.state === 'hasData' ? loadable.data : null;
}

export function useView() {
  const updateSelectedView = useUpdateSelectedView();

  const getSection = useAtomCallback(
    useCallback((get, _set, section: ViewSectionUuid) => {
      const view = readSelectedViewSync(get);

      if (!view?.sections?.[section]) {
        throw new Error(`There is no ${section} view section config!`);
      }

      return view.sections[section];
    }, [])
  );

  const getSectionsOrder = useAtomCallback(
    useCallback((get) => {
      const view = readSelectedViewSync(get);
      if (!view?.order?.sections) {
        return [];
      }

      return view.order.sections;
    }, [])
  );

  const getComponentsOrder = useAtomCallback(
    useCallback((get, _set, section: ViewSectionUuid) => {
      const view = readSelectedViewSync(get);
      if (!view?.order?.components?.[section]) {
        return [];
      }
      return view.order.components[section];
    }, [])
  );

  const setComponentsOrder = useAtomCallback(
    useCallback(
      (get, _set, section: ViewSectionUuid, order: ViewComponentUuid[]) => {
        const view = readSelectedViewSync(get);
        if (view) {
          // view.order is undefined at runtime for views seeded with an empty config (e.g. fund_overview).
          updateSelectedView({
            order: { ...view.order, components: { ...view.order?.components, [section]: order } },
          });
        }
      },
      [updateSelectedView]
    )
  );

  const getFundsFilter = useAtomCallback(
    useCallback((get) => {
      const view = readSelectedViewSync(get);
      return view?.filteredFunds || null;
    }, [])
  );

  const setFundsFilter = useCallback(
    (filteredFunds: IFundDataModel[] | null) => {
      updateSelectedView({ filteredFunds });
    },
    [updateSelectedView]
  );

  return useMemo(
    () => ({
      isLoading: false,

      getSection,
      getSectionsOrder,
      getComponentsOrder,
      setComponentsOrder,
      isFetching: false,
      setFundsFilter,
      getFundsFilter,
    }),
    [getSection, getSectionsOrder, getComponentsOrder, setComponentsOrder, setFundsFilter, getFundsFilter]
  );
}
